import { useState } from "react";
import { useCart } from "./shopping-cart-provider";
import { Button } from "./ui/button";
import { Loader2Icon, ShoppingCartIcon } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { Badge } from "./ui/badge";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { CartList } from "./cart-list";
import { useAuth } from "@/hooks/auth";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  createRequestMutation,
  getAllRequestsForCustomerQueryKey,
} from "@/client/@tanstack/react-query.gen";
import { toast } from "sonner";
import { useCustomerId } from "./customer-id-provider";
import { HttpError } from "@/http-error";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "./ui/form";
import { Textarea } from "./ui/textarea";
import { cn } from "@/lib/utils";
import { useResponsive, useIsExtraSmall } from "@/hooks/useIsMobile";

const createCheckoutSchema = (
  t: TFunction<"translation", undefined>,
  maxCommentLength: number
) =>
  z.object({
    comment: z
      .string()
      .max(
        maxCommentLength,
        t("cart.commentMaxLength", { maxLength: maxCommentLength })
      )
      .optional(),
  });

type CheckoutFormData = z.infer<ReturnType<typeof createCheckoutSchema>>;

interface ShoppingCartProps {
  maxCommentLength?: number;
}

export default function ShoppingCart({
  maxCommentLength = 1000,
}: ShoppingCartProps) {
  const queryClient = useQueryClient();
  const { customerId } = useCustomerId();
  const { totalItems, items, emptyCart, cartSessionId } = useCart();
  const [t] = useTranslation();
  const { isLoading, isLangguthUser } = useAuth();
  const [open, setOpen] = useState(false);
  const { breakpoint } = useResponsive();
  const isExtraSmall = useIsExtraSmall();

  const formSchema = createCheckoutSchema(t, maxCommentLength);
  const form = useForm<CheckoutFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      comment: "",
    },
    mode: "onTouched",
  });

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (newOpen) {
      form.reset();
    }
  };

  const checkoutMutation = useMutation({
    ...createRequestMutation(),
    onSuccess: () => {
      toast.success(t("cart.checkoutSuccess"));
      form.reset();
      emptyCart();
      queryClient.invalidateQueries({
        queryKey: getAllRequestsForCustomerQueryKey({
          path: {
            customer_id: customerId!,
          },
        }),
      });
      //next render cycle
      setTimeout(() => {
        setOpen(false);
      }, 0);
    },
    onError: (error) => {
      const message =
        error instanceof HttpError && error.status === 408
          ? t("error.http.408")
          : t("cart.checkoutError");
      toast.error(message);
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: getAllRequestsForCustomerQueryKey({
          path: {
            customer_id: customerId!,
          },
        }),
      });
    },
  });

  const onSubmit = (data: CheckoutFormData) => {
    checkoutMutation.mutate({
      path: { customer_id: customerId! },
      body: {
        items: items.map((item) => ({
          asMiId: item.asMiId ?? "",
          machineId: item.machineId ?? "",
          level1AncestorItemId: item.level1AncestorItemId ?? "",
          quantity: item.quantity ?? 1,
          comment: isLangguthUser ? item.comment?.trim() || undefined : undefined,
        })),
        comment: data.comment || undefined,
        cartSessionId: cartSessionId || undefined,
      },
    });
  };

  if (isLoading || !customerId) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <div className="relative mr-2 shrink-0">
          {!!totalItems && totalItems > 0 && (
            <Badge className="absolute -right-1 -top-1">{totalItems}</Badge>
          )}
          <Button variant="outline" size="icon">
            <ShoppingCartIcon></ShoppingCartIcon>
          </Button>
        </div>
      </DialogTrigger>
      <DialogContent
        className={cn(
          "flex flex-col overflow-hidden min-w-0",
          isExtraSmall
            ? "!w-[98vw] !max-w-[98vw] !h-[95dvh] p-2"
            : breakpoint === "sm"
            ? "!w-[96vw] !max-w-[96vw] !h-[90dvh] p-4"
            : "!w-[90vw] !max-w-7xl !h-[85vh] p-6"
        )}
      >
        <DialogHeader>
          <DialogTitle className={cn(
            isExtraSmall ? "text-sm" : "text-base md:text-lg"
          )}>
            {t("cart.title")}
          </DialogTitle>
          <DialogDescription className={cn(
            isExtraSmall ? "text-xs" : "text-sm"
          )}>
            {t("cart.description")}
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 min-h-0 min-w-0 overflow-hidden">
          <CartList />
        </div>
        <div className="flex-shrink-0 mt-2 pt-2 border-t">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className={cn(
              isExtraSmall ? "space-y-2" : "space-y-4"
            )}>
              <FormField
                control={form.control}
                name="comment"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={cn(
                      isExtraSmall ? "text-xs" : "text-sm"
                    )}>
                      {t("cart.comment")}
                    </FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder={t("cart.commentPlaceholder")}
                        className={cn(
                          isExtraSmall ? "min-h-[60px] text-xs" : "min-h-[100px]"
                        )}
                        {...field}
                      />
                    </FormControl>
                    <div className="flex justify-between items-center text-sm">
                      <FormMessage />
                      <span
                        className={cn(
                          "text-muted-foreground ml-auto",
                          isExtraSmall ? "text-xs" : "text-sm",
                          field.value &&
                            field.value.length > maxCommentLength * 0.9 &&
                            "text-amber-600",
                          field.value &&
                            field.value.length > maxCommentLength &&
                            "text-red-600"
                        )}
                      >
                        {field.value?.length || 0}/{maxCommentLength}
                      </span>
                    </div>
                  </FormItem>
                )}
              />
              <Button
                type="submit"
                size={isExtraSmall ? "sm" : "default"}
                disabled={
                  checkoutMutation.isPending ||
                  items.length === 0 ||
                  !form.formState.isValid
                }
                className="w-full"
              >
                {checkoutMutation.isPending ? (
                  <>
                    <Loader2Icon className={cn(
                      "animate-spin",
                      isExtraSmall ? "mr-1 h-3 w-3" : "mr-2 h-4 w-4"
                    )} />
                    {t("cart.checkingOut")}
                  </>
                ) : (
                  t("cart.checkout")
                )}
              </Button>
            </form>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
